import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { Building2, Plus, Users } from 'lucide-react';

import API_URL from '../utils/api';

const Departments = () => {
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [deptRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/departments`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true })
            ]);
            setDepartments(deptRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/departments`, formData, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ name: '', description: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating department');
        }
    };

    const getEmployeeCount = (deptName) => {
        return employees.filter(e => e.department === deptName).length;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading departments...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="departments-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Departments
                    </h1>
                    <p className="text-slate-500 mt-1">Manage organization structure</p>
                </div>
                
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-department-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Department
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Create Department
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Department Name</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="rounded-none mt-1"
                                    required
                                    data-testid="department-name-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="rounded-none mt-1"
                                    rows={3}
                                    data-testid="department-description-input"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="department-save-btn">
                                    Create Department
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Department Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {departments.map((dept, index) => (
                    <Card key={index} className="rounded-none border-slate-200 hover:border-blue-300 transition-colors" data-testid={`department-card-${dept.name}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div className="p-3 bg-blue-100">
                                    <Building2 className="h-6 w-6 text-brand" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mt-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {dept.name}
                            </h3>
                            <p className="text-slate-500 text-sm mt-2">
                                {dept.description || 'No description'}
                            </p>
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                                <Users className="h-4 w-4 text-slate-400" />
                                <span className="text-sm text-slate-600">
                                    {getEmployeeCount(dept.name)} employees
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {departments.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500">
                        No departments found. Create one to get started.
                    </div>
                )}
            </div>
        </div>
    );
};

export default Departments;
